import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { BadgeCheck, XCircle, CreditCard, Loader2 } from "lucide-react";

const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;

export default function QuotePage() {
  const { token } = useParams();
  const [quote, setQuote] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/quotes/${token}`);
      setQuote(data);
    } catch {
      setError("This quote link is invalid or has expired.");
    }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const act = async (path) => {
    setBusy(path);
    try {
      const { data } = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/quotes/${token}/${path}`);
      setQuote(data);
      toast.success(path === "accept" ? "Quote accepted — thank you!" : "Quote declined");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Something went wrong");
    } finally { setBusy(""); }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-paper px-5 py-16 text-teal-anchor">
      <div className="w-full max-w-xl">
        <p className="font-serif text-2xl tracking-tight">Tanonshop</p>
        <p className="text-[10px] uppercase tracking-[0.2em] text-teal-anchor/50">Custom cushion covers</p>

        {error && <div data-testid="quote-error" className="mt-10 rounded-sm border border-teal-anchor/12 bg-white p-8 text-center text-teal-anchor/70">{error}</div>}

        {!error && !quote && <div className="mt-10 flex justify-center p-10"><Loader2 className="animate-spin text-teal-primary" /></div>}

        {quote && (
          <div data-testid="quote-page" className="mt-8 rounded-sm border border-teal-anchor/12 bg-white p-7 sm:p-9">
            <p className="text-xs font-semibold uppercase tracking-[0.15em] text-teal-primary">Your custom quote</p>
            <h1 className="mt-3 font-serif text-3xl tracking-tight">Hi {quote.customer_name},</h1>
            <p className="mt-2 text-sm text-teal-anchor/70">Here's the quote we prepared for you.</p>

            {quote.template && quote.template !== "simple" ? (
              <div className="mt-6 text-sm" data-testid="quote-detailed">
                {quote.quote_date && <p className="text-xs text-teal-anchor/50">DATE: {quote.quote_date}</p>}
                {quote.template === "tsn_provides" && quote.material_rows?.length > 0 && (
                  <div className="mt-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.13em] text-teal-primary">Material supplied by Tanonshop</p>
                    {quote.material_rows.map((m, x) => (
                      <div key={x} className="mt-1 flex justify-between gap-4"><span className="text-teal-anchor/80">{m.name} ({m.qty} yd)</span><span>{money(m.qty * m.price_per_yard + (m.tax || 0))}</span></div>
                    ))}
                    <div className="mt-1 flex justify-between gap-4 border-t border-teal-anchor/10 pt-1 font-semibold"><span>Material total</span><span>{money(quote.totals?.material_total)}</span></div>
                  </div>
                )}
                <div className="mt-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.13em] text-teal-primary">Supply - Labor fee</p>
                  {quote.cover_rows?.map((r, x) => (
                    <div key={x} className="mt-1 flex justify-between gap-4">
                      <span className="text-teal-anchor/80">{r.name} ({r.dimensions}) ×{r.qty}</span>
                      <span>{money((Number(r.supply) + Number(r.piping) + Number(r.labor)) * Number(r.qty))}</span>
                    </div>
                  ))}
                  <div className="mt-1 flex justify-between gap-4 border-t border-teal-anchor/10 pt-1"><span>Subtotal</span><span>{money(quote.totals?.cover_subtotal)}</span></div>
                  <div className="flex justify-between gap-4"><span>Shipping</span><span>{money(quote.totals?.shipping)}</span></div>
                  <div className="flex justify-between gap-4 font-semibold">
                    <span>{quote.template === "tsn_provides" ? "Cover & shipping total" : "Total quoted"}</span>
                    <span>{money(quote.totals?.cover_shipping_total ?? quote.subtotal)}</span>
                  </div>
                </div>
              </div>
            ) : (
              <ul className="mt-6 space-y-2 text-sm">
                {quote.items.map((i, x) => (
                  <li key={x} className="flex justify-between gap-8"><span className="text-teal-anchor/80">{i.description}</span><span>{money(i.amount)}</span></li>
                ))}
              </ul>
            )}
            <div className="mt-4 space-y-1 border-t border-teal-anchor/10 pt-3 text-sm">
              <div className="flex justify-between"><span className="text-teal-anchor/60">Subtotal</span><span>{money(quote.subtotal)}</span></div>
              {quote.discount_amount > 0 && (
                <div data-testid="quote-discount" className="flex justify-between text-teal-primary"><span>First-order discount ({quote.discount_code})</span><span>-{money(quote.discount_amount)}</span></div>
              )}
              <div className="flex justify-between pt-1 font-serif text-xl"><span>Total</span><span>{money(quote.total)}</span></div>
            </div>
            {quote.notes && <p className="mt-4 rounded-sm bg-paper-soft p-3 text-sm text-teal-anchor/70">{quote.notes}</p>}

            <div className="mt-7">
              {quote.status === "sent" && (
                <div className="flex flex-col gap-3 sm:flex-row">
                  <button data-testid="quote-accept-btn" onClick={() => act("accept")} disabled={!!busy}
                    className="flex flex-1 items-center justify-center gap-2 rounded-sm bg-teal-primary px-5 py-3.5 text-xs font-semibold uppercase tracking-[0.15em] text-paper hover:bg-teal-hover disabled:opacity-60">
                    <BadgeCheck size={16} /> Accept quote
                  </button>
                  <button data-testid="quote-decline-btn" onClick={() => act("decline")} disabled={!!busy}
                    className="flex flex-1 items-center justify-center gap-2 rounded-sm border border-teal-anchor/20 px-5 py-3.5 text-xs font-semibold uppercase tracking-[0.15em] hover:border-teal-primary disabled:opacity-60">
                    <XCircle size={16} /> Decline
                  </button>
                </div>
              )}
              {quote.status === "accepted" && quote.payment_status !== "paid" && (
                <div data-testid="quote-accepted" className="rounded-sm bg-teal-primary/8 p-4 text-sm">
                  <p className="font-semibold text-teal-primary">Quote accepted — thank you!</p>
                  {quote.invoice_url ? (
                    <a data-testid="quote-pay-btn" href={quote.invoice_url} target="_blank" rel="noreferrer"
                      className="mt-3 inline-flex items-center gap-2 rounded-sm bg-teal-primary px-5 py-3 text-xs font-semibold uppercase tracking-[0.15em] text-paper hover:bg-teal-hover">
                      <CreditCard size={16} /> Pay invoice securely
                    </a>
                  ) : (
                    <p className="mt-1 text-teal-anchor/70">We'll email your secure invoice shortly.</p>
                  )}
                </div>
              )}
              {quote.payment_status === "paid" && (
                <div data-testid="quote-paid" className="rounded-sm bg-teal-primary/10 p-4 text-sm font-semibold text-teal-primary">Payment received — your order is now in production. We'll email you when it ships.</div>
              )}
              {quote.status === "declined" && (
                <div data-testid="quote-declined" className="rounded-sm bg-paper-soft p-4 text-sm text-teal-anchor/70">This quote was declined. Changed your mind? Just reply to our email — we're happy to help.</div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
